import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaign, useNoteIndex, usePrefetchNote, useTypes } from '../hooks';
import { t } from '../i18n';
import { classNames, fuzzyFilter, splitTarget } from '../util';
import { useNewNote } from './NewNoteDialog';

/** Strg+K: Schnellsprung über Titel. Filtert lokal, daher ohne Netzwerk-Latenz. */
export function QuickSwitcher({ cid }: { cid: string }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K' || e.key === 'o')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('kt:quick-switcher', onEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('kt:quick-switcher', onEvent);
    };
  }, []);

  return open ? <QuickSwitcherDialog cid={cid} onClose={() => setOpen(false)} /> : null;
}

function QuickSwitcherDialog({ cid, onClose }: { cid: string; onClose: () => void }) {
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const campaign = useCampaign(cid);
  const navigate = useNavigate();
  const prefetch = usePrefetchNote(cid);
  const newNote = useNewNote();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const gm = !!campaign.data && campaign.data.role !== 'player';

  const results = useMemo(() => fuzzyFilter(index.data ?? [], q, 50), [index.data, q]);
  const exact = results.some((r) => r.title.toLowerCase() === q.trim().toLowerCase());
  const extra = q.trim() ? (gm && !exact ? 2 : 1) : 0;
  const total = results.length + extra;

  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    const r = results[sel];
    if (r) prefetch(r.id);
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [sel, results]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (i: number, forceCreate = false) => {
    if (forceCreate && gm && q.trim()) return create();
    const r = results[i];
    if (r) {
      navigate(`/c/${cid}/n/${r.id}`);
      onClose();
      return;
    }
    const rel = i - results.length;
    if (gm && !exact && rel === 0) return create();
    navigate(`/c/${cid}/search?q=${encodeURIComponent(q.trim())}`);
    onClose();
  };

  const create = () => {
    const { folder, title } = splitTarget(q.trim());
    onClose();
    newNote.open({ title, folder, onCreated: (id) => navigate(`/c/${cid}/n/${id}?edit=1`) });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(total - 1, s + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(sel, e.ctrlKey || e.metaKey);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop top" onMouseDown={onClose}>
      <div className="quick" onMouseDown={(e) => e.stopPropagation()}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder={t('quick.placeholder')} />
        <ul ref={listRef}>
          {results.map((r, i) => (
            <li
              key={r.id}
              aria-selected={i === sel}
              className={classNames(i === sel && 'sel')}
              onMouseEnter={() => setSel(i)}
              onClick={() => choose(i)}
            >
              <span className="type-dot" style={{ background: types.byKey.get(r.typeKey)?.color }} />
              <span className="quick-title">{r.title}</span>
              {r.folder && <span className="muted small">{r.folder}</span>}
            </li>
          ))}
          {extra > 0 && gm && !exact && (
            <li
              aria-selected={sel === results.length}
              className={classNames('action', sel === results.length && 'sel')}
              onMouseEnter={() => setSel(results.length)}
              onClick={create}
            >
              + {t('quick.create', { title: q.trim() })}
            </li>
          )}
          {extra > 0 && (
            <li
              aria-selected={sel === total - 1}
              className={classNames('action', sel === total - 1 && 'sel')}
              onMouseEnter={() => setSel(total - 1)}
              onClick={() => choose(total - 1)}
            >
              ⌕ {t('quick.fulltext', { q: q.trim() })}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
